import { useId, useState } from 'react';
import {
  coerceFieldValue,
  fieldsForCategory,
  formatFieldValue,
  valueFor,
  type AnyEvent,
  type CustomFieldDef,
  type CustomFieldType,
  type FieldValue,
  type ItemRecord,
  type Manifest,
} from '@intentionalist/shared';
import { defineField, deleteField, setFieldValue } from './mutations.js';

/**
 * An item's custom field answers, plus a way to define new ones (Epic 4).
 *
 * Each answer is saved as it is given rather than when the surrounding form is
 * submitted, like tags and photos -- a custom field is an edit to the item, and
 * batching it into the form would mean an answer could be lost by cancelling.
 *
 * Definitions are made from here for the same reason categories are made from
 * the picker: the moment someone wants to record a thing's sleeve length is the
 * moment they discover the field does not exist, and sending them to a settings
 * screen is how that detail goes unrecorded.
 */
const TYPES: { value: CustomFieldType; label: string }[] = [
  { value: 'text', label: 'Text' },
  { value: 'number', label: 'Number' },
  { value: 'date', label: 'Date' },
  { value: 'boolean', label: 'Yes / no' },
  { value: 'single-select', label: 'One of a list' },
  { value: 'multi-select', label: 'Several of a list' },
];

export function CustomFields({
  manifest,
  item,
  categoryId: pending,
  onWrite,
}: {
  manifest: Manifest;
  item: ItemRecord;
  /**
   * The category to ask about, when a form alongside has one chosen that the
   * item has not been saved with yet. Absent means the item's own.
   *
   * Filing an item is how its fields are discovered, so the questions have to
   * follow the picker rather than the saved record: without this, choosing a
   * category here showed nothing until the item was saved and reopened.
   */
  categoryId?: string | null | undefined;
  onWrite: (events: AnyEvent[]) => void;
}) {
  const asCategory = (value: unknown): string | null =>
    typeof value === 'string' && value !== '' ? value : null;

  const categoryId =
    pending === undefined ? asCategory(item.fields.categoryId?.v) : asCategory(pending);

  const defs = fieldsForCategory(manifest, categoryId);
  const [defining, setDefining] = useState(false);
  const headingId = useId();

  if (categoryId === null) {
    return (
      <section aria-labelledby={headingId} className="field">
        <h3 id={headingId}>Custom fields</h3>
        {/* A definition belongs to a category, so there is nowhere to put one
            until the item is filed. Saying so beats an empty panel. */}
        <p className="muted">File this item in a category to add fields of your own.</p>
      </section>
    );
  }

  return (
    <section aria-labelledby={headingId} className="custom-fields">
      <h3 id={headingId}>Custom fields</h3>

      {defs.length === 0 && !defining && (
        <p className="muted">No fields defined for this category yet.</p>
      )}

      {defs.map((def) => (
        <FieldInput
          key={def.fieldId}
          def={def}
          value={valueFor(manifest, item, def)}
          onChange={(value) => onWrite(setFieldValue(item.itemId, def.fieldId, value))}
          onDelete={() => onWrite(deleteField(def.fieldId, def.categoryId))}
        />
      ))}

      {defining ? (
        <DefineField
          categoryId={categoryId}
          onCreate={(events) => {
            onWrite(events);
            setDefining(false);
          }}
          onCancel={() => setDefining(false)}
        />
      ) : (
        <button type="button" className="link" onClick={() => setDefining(true)}>
          + New field
        </button>
      )}
    </section>
  );
}

function FieldInput({
  def,
  value,
  onChange,
  onDelete,
}: {
  def: CustomFieldDef;
  value: unknown;
  onChange: (value: FieldValue) => void;
  onDelete: () => void;
}) {
  const id = useId();
  const [error, setError] = useState<string | null>(null);

  const commit = (raw: string | string[] | boolean) => {
    const result = coerceFieldValue(def, raw);
    if (!result.ok) {
      setError(result.error);
      return;
    }
    setError(null);
    onChange(result.value);
  };

  const label = (
    <label htmlFor={id}>
      {def.name}
      {/* Says where an inherited field comes from, so deleting it from the wrong
          place is not a surprise. */}
      {def.inheritedFrom > 1 && <span className="muted"> (inherited)</span>}
    </label>
  );

  return (
    <div className="field">
      {label}

      {def.type === 'boolean' ? (
        <input
          id={id}
          type="checkbox"
          checked={value === true}
          onChange={(e) => commit(e.target.checked)}
        />
      ) : def.type === 'single-select' ? (
        <select
          id={id}
          defaultValue={typeof value === 'string' ? value : ''}
          onChange={(e) => commit(e.target.value)}
        >
          <option value="">Not set</option>
          {def.options.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
      ) : def.type === 'multi-select' ? (
        <select
          id={id}
          multiple
          defaultValue={Array.isArray(value) ? (value as string[]) : []}
          onChange={(e) => commit([...e.target.selectedOptions].map((o) => o.value))}
        >
          {def.options.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
      ) : (
        <input
          id={id}
          type={def.type === 'date' ? 'date' : 'text'}
          inputMode={def.type === 'number' ? 'decimal' : undefined}
          defaultValue={formatFieldValue(value)}
          // On blur rather than on every keystroke: each change is an event in
          // the log, and one per character would be a lot of history for nothing.
          onBlur={(e) => commit(e.target.value)}
        />
      )}

      {error !== null && (
        <p className="warn hint" role="alert">
          {error}
        </p>
      )}

      <button type="button" className="link" onClick={onDelete}>
        Remove this field from {def.inheritedFrom > 1 ? 'its category' : 'this category'}
      </button>
    </div>
  );
}

function DefineField({
  categoryId,
  onCreate,
  onCancel,
}: {
  categoryId: string;
  onCreate: (events: AnyEvent[]) => void;
  onCancel: () => void;
}) {
  const id = useId();
  const [name, setName] = useState('');
  const [type, setType] = useState<CustomFieldType>('text');
  const [options, setOptions] = useState('');

  const selectable = type === 'single-select' || type === 'multi-select';
  const parsed = options
    .split(',')
    .map((option) => option.trim())
    .filter((option) => option !== '');

  const create = () => {
    if (name.trim() === '') return;
    if (selectable && parsed.length === 0) return;
    onCreate(defineField({ categoryId, name, type, options: parsed }).events);
  };

  return (
    <div className="field">
      <label htmlFor={`${id}-name`}>Field name</label>
      <input
        id={`${id}-name`}
        value={name}
        onChange={(e) => setName(e.target.value)}
        autoComplete="off"
      />

      <label htmlFor={`${id}-type`}>Type</label>
      <select
        id={`${id}-type`}
        value={type}
        onChange={(e) => setType(e.target.value as CustomFieldType)}
      >
        {TYPES.map((entry) => (
          <option key={entry.value} value={entry.value}>
            {entry.label}
          </option>
        ))}
      </select>

      {selectable && (
        <>
          <label htmlFor={`${id}-options`}>Choices</label>
          <input
            id={`${id}-options`}
            value={options}
            onChange={(e) => setOptions(e.target.value)}
            placeholder="slim, regular, relaxed"
          />
          <p className="muted hint">Separated by commas.</p>
        </>
      )}

      <div className="actions">
        <button
          type="button"
          onClick={create}
          // A select with no choices is a field nothing can be answered with.
          disabled={name.trim() === '' || (selectable && parsed.length === 0)}
        >
          Create
        </button>
        <button type="button" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </div>
  );
}
